import { z } from 'zod';
import { tagNameSchema } from '../tag/tag';
import {
  difficultySchema,
  explanationSchema,
  languageLimitSchema,
  languageSchema,
  numericAnswerSchema,
  rejectionReasonSchema,
  sourceReferenceSchema,
  Task,
  taskIdSchema,
  taskSchema,
  taskStatusSchema,
  taskTranslationSchema,
  taskTypeSchema,
  taskTypesSchema,
  tagsSchema,
  timestampSchema,
} from './task';

/** The task bank's collection path (QZ-D42). */
export const TASKS_PATH = '/tasks';

/** A single task's path (QZ-D42). */
export const taskPath = (taskId: string): string => `/tasks/${encodeURIComponent(taskId)}`;

/** A single task's status path (QZ-D37). */
export const taskStatusPath = (taskId: string): string => `${taskPath(taskId)}/status`;

/** The router pattern matching `taskPath` (QZ-D42). */
export const TASK_PATH_PATTERN = '/tasks/:taskId';

/** The router pattern matching `taskStatusPath` (QZ-D37). */
export const TASK_STATUS_PATH_PATTERN = '/tasks/:taskId/status';

/** The number of tasks returned by one page of `GET /tasks` (QZ-D38). */
export const TASK_PAGE_SIZE = 25;

/** The query string accepted by `GET /tasks` (QZ-D42). */
export const listTasksQuerySchema = z
  .object({
    status: taskStatusSchema.default('draft'),
    tag: tagNameSchema.optional(),
    taskType: taskTypeSchema.optional(),
    difficulty: difficultySchema.optional(),
    language: languageSchema.optional(),
    jobId: taskIdSchema.optional(),
    cursor: z.string().min(1).optional(),
  })
  .strict();
export type ListTasksQuery = z.infer<typeof listTasksQuerySchema>;

/** The body returned by `GET /tasks` (QZ-D42). */
export const listTasksResponseSchema = z
  .object({
    tasks: z.array(taskSchema),
    nextCursor: z.string().min(1).optional(),
  })
  .strip();
export type ListTasksResponse = z.infer<typeof listTasksResponseSchema>;

/** The body accepted by `PUT /tasks/:taskId` to replace a draft's editable content (QZ-D41). */
export const updateTaskRequestSchema = z
  .object({
    expectedUpdatedAt: timestampSchema,
    taskTypes: taskTypesSchema,
    difficulty: difficultySchema,
    tags: tagsSchema,
    languageLimit: languageLimitSchema.optional(),
    numericAnswer: numericAnswerSchema.optional(),
    sourceReference: sourceReferenceSchema.optional(),
    explanation: explanationSchema,
    original: taskTranslationSchema,
  })
  .strict();
export type UpdateTaskRequest = z.infer<typeof updateTaskRequestSchema>;

/** The body accepted by `POST /tasks/:taskId/status` (QZ-D37). */
export const changeTaskStatusRequestSchema = z
  .object({
    status: taskStatusSchema,
    rejectionReason: rejectionReasonSchema.optional(),
    expectedUpdatedAt: timestampSchema,
  })
  .strict()
  .superRefine((value, ctx) => {
    if (value.rejectionReason !== undefined && value.status !== 'rejected') {
      ctx.addIssue({
        code: 'custom',
        path: ['rejectionReason'],
        message: 'A rejection reason is only allowed when rejecting.',
      });
    }
  });
export type ChangeTaskStatusRequest = z.infer<typeof changeTaskStatusRequestSchema>;

/**
 * The stored task with a review edit applied (QZ-D41): the request's editable fields replace
 * the task's, `original` replaces `translations[task.originalLanguage]`, optional fields the
 * request omits are removed, and `updatedAt` is set. The result is unvalidated (QZ-D42).
 */
export const applyTaskUpdate = (
  task: Task,
  request: UpdateTaskRequest,
  updatedAt: string
): unknown => {
  const { languageLimit: _limit, numericAnswer: _number, sourceReference: _source, ...kept } = task;
  return {
    ...kept,
    taskTypes: request.taskTypes,
    difficulty: request.difficulty,
    tags: request.tags,
    ...(request.sourceReference === undefined ? {} : { sourceReference: request.sourceReference }),
    explanation: request.explanation,
    ...(request.languageLimit === undefined ? {} : { languageLimit: request.languageLimit }),
    ...(request.numericAnswer === undefined ? {} : { numericAnswer: request.numericAnswer }),
    translations: { ...task.translations, [task.originalLanguage]: request.original },
    updatedAt,
  };
};
